export type RelayState = "active" | "disabled" | "retired";
export type GatewayKeyState = "active" | "disabled" | "revoked";
export type QuotaStatus = "fresh" | "stale" | "unavailable";

export interface Relay {
  id: string;
  displayName: string;
  baseURL: string;
  stateFingerprint: string;
  state: RelayState;
  createdAtMS: number;
  updatedAtMS: number;
}

export interface KeyLimits {
  concurrency: number | null;
  rpm: number | null;
}

export interface GatewayKey {
  id: string;
  publicID: string;
  label: string;
  state: GatewayKeyState;
  authVersion: number;
  limits: KeyLimits;
  createdAtMS: number;
  updatedAtMS: number;
  eligibleRelayIDs: string[];
}

export interface HTTPStats {
  requestsTotal: number;
  inFlight: number;
  canceled: number;
  status2xx: number;
  status4xx: number;
  status5xx: number;
  otherStatus: number;
  responseBytes: number;
}

export interface RuntimeProcess {
  uptimeMS: number;
  residentMemoryBytes: number | null;
  virtualMemoryBytes: number | null;
  cpuUserMS: number | null;
  cpuSystemMS: number | null;
  threads: number | null;
  openFileDescriptors: number | null;
}

export interface TransformationExecution {
  workers: number;
  active: number;
  queueDepth: number;
  queueCapacity: number;
}

export interface DatabaseExecution {
  readLanes: number;
  readActive: number;
  readQueueDepth: number;
  readQueueCapacity: number;
  writeLanes: number;
  writeActive: number;
  writeQueueDepth: number;
  writeQueueCapacity: number;
}

export interface RuntimeSnapshot {
  schemaVersion: 1;
  runtimeKind: "rust";
  collectionStatus: "complete" | "partial";
  process: RuntimeProcess;
  execution: {
    tasksActive: number;
    ioWorkers: number;
    transformation: TransformationExecution | null;
    database: DatabaseExecution | null;
    requestBodyDecoder: TransformationExecution | null;
  };
}

export type RuntimePanel =
  | { status: "available"; snapshot: RuntimeSnapshot }
  | { status: "unsupported"; schemaVersion: number | null; runtimeKind: string | null }
  | { status: "invalid" }
  | { status: "unavailable" };

export interface GatewayStatus {
  meter: {
    accepted: number;
    queueDropped: number;
    hotWritten: number;
    hotWriteFailures: number;
    hotDropped: number;
    coldArchived: number;
    coldDropped: number;
    pending: number;
    coldAvailable: boolean;
    accepting: boolean;
    writerHealthy: boolean;
    degraded: boolean;
  };
  operational: {
    http: HTTPStats;
    admission: {
      globalInFlight: number;
      activeKeys: number;
      keyInFlight: number;
      searchInFlight: number;
    };
    runtime: RuntimePanel;
  } | null;
}

export interface RelayStatusView {
  relayID: string;
  stateFingerprint: string;
  credentialProvider: string;
  upstreamAccountID: string | null;
  credentialGeneration: number | null;
  credentialExpiresAtMS: number | null;
  credentialStatus: "available" | "unavailable";
  operational: {
    http: HTTPStats;
    admission: {
      transformation: number;
      operational: number;
      waiting: number;
    };
    transformation: TransformationExecution;
    runtime: RuntimePanel;
  };
}

export interface QuotaWindow {
  kind: string;
  usedPercent: number;
  durationSeconds: number;
  resetsAt: number;
}

export interface QuotaLimit {
  id: string;
  name: string | null;
  windows: QuotaWindow[];
}

export interface QuotaView {
  relayID: string;
  status: QuotaStatus;
  observedAt: string | null;
  planType: string | null;
  limits: QuotaLimit[];
  refreshFailed: boolean;
}

export interface MeterRecord {
  requestID: string;
  gatewayKeyID: string;
  sessionBindingID: string;
  relayID: string;
  routeKind: "responses" | "search";
  startedAtMS: number;
  durationMS: number;
  ttfbMS: number | null;
  httpStatus: number;
  outcome:
    | "completed"
    | "relay_error"
    | "relay_failure"
    | "stream_error"
    | "downstream_error"
    | "canceled";
  model: string | null;
  requestBytes: number;
  responseBytes: number;
  usageStatus: "reported" | "missing" | "parse_error";
  tokens: {
    input: number | null;
    output: number | null;
    total: number | null;
    cacheRead: number | null;
    cacheWrite: number | null;
  };
}

export interface AuditEvent {
  id: number;
  action: string;
  subjectType: string;
  subjectID: string;
  result: string;
  metadata: Record<string, unknown> | null;
  createdAtMS: number;
}

export interface ActivitySummary {
  records: number;
  success: number;
  clientErrors: number;
  failures: number;
  knownTokenRecords: number;
  tokenCoveragePercent: number | null;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
}

export interface ObservedTokenTotal {
  sum: string | null;
  knownRecords: number;
}

export type ObservedUsageGroup = "all" | "key" | "relay";

export type ObservedUsageSubject =
  | { kind: "all" }
  | {
    kind: "key";
    internalID: string;
    publicID: string;
    label: string;
    state: GatewayKeyState;
  }
  | { kind: "relay"; internalID: string; displayName: string; state: RelayState };

export interface ObservedUsageRow {
  subject: ObservedUsageSubject;
  requests: {
    total: number;
    responses: number;
    search: number;
    completed: number;
    abnormal: number;
  };
  usage: {
    reported: number;
    missing: number;
    parseError: number;
    reportedWithoutTotal: number;
  };
  tokens: {
    input: ObservedTokenTotal;
    output: ObservedTokenTotal;
    total: ObservedTokenTotal;
    cacheRead: ObservedTokenTotal;
    cacheWrite: ObservedTokenTotal;
  };
  tokenCoverage: { known: number; eligible: number } | null;
}

export const observedUsageWindows = [
  { value: "24h", label: "24 小时", durationMS: 86_400_000 },
  { value: "7d", label: "7 天", durationMS: 604_800_000 },
  { value: "30d", label: "30 天", durationMS: 2_592_000_000 },
] as const;

export type ObservedUsageWindow = (typeof observedUsageWindows)[number]["value"];

export function observedUsageWindowDefinition(value: unknown): (typeof observedUsageWindows)[number] | null {
  return observedUsageWindows.find((window) => window.value === value) ?? null;
}

export interface ObservedUsage {
  schemaVersion: 1;
  window: ObservedUsageWindow;
  groupBy: ObservedUsageGroup;
  generatedAtMS: number;
  startAtMS: number;
  endAtMS: number;
  hotHistory: {
    count: number;
    capacity: number;
    oldestCompletionAtMS: number | null;
    windowStatus: "complete" | "partial";
  };
  rows: ObservedUsageRow[];
}
